// Ported from design_ideas/lernwelt/bundle.jsx — tap-card multiple choice

export interface LanternOption {
  label: string;
  value: string;
  /** Undefined until the server has graded the answer. */
  correct?: boolean;
}

interface Props {
  options: LanternOption[];
  selected: string | null;
  setSelected: (v: string) => void;
  submitted: boolean;
  disabled: boolean;
}

export default function LanternPicker({ options, selected, setSelected, submitted, disabled }: Props) {
  const maxLen = Math.max(...options.map(o => o.label.length));
  const labelSize = maxLen <= 3 ? 34 : maxLen <= 8 ? 22 : maxLen <= 16 ? 16 : 13;

  // Grid columns scale to label length so long options wrap into multiple
  // rows instead of forcing a sideways scroll. Short numeric/word options
  // stay in a single row; sentence-length labels go to 2 columns or 1.
  const minColWidth =
    maxLen <= 4 ? '64px' :
    maxLen <= 12 ? '120px' :
    maxLen <= 24 ? '180px' :
    '240px';
  const gridColumns =
    maxLen <= 4
      ? `repeat(${options.length}, minmax(0, 1fr))`
      : `repeat(auto-fit, minmax(min(${minColWidth}, 100%), 1fr))`;

  return (
    <div
      role="radiogroup"
      aria-label="Antwortmöglichkeiten"
      style={{
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gap: 14,
        marginBottom: 20,
      }}
    >
      {options.map((opt, i) => {
        const isSelected = selected === opt.value;
        const isRight = submitted && opt.correct === true;
        const isWrong = submitted && isSelected && opt.correct === false;
        const letter = String.fromCharCode(65 + i);

        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={isSelected}
            aria-label={`Antwort ${letter}: ${opt.label}`}
            onClick={() => !submitted && !disabled && setSelected(opt.value)}
            disabled={submitted || disabled}
            style={{
              minHeight: 72,
              minWidth: 0,
              position: 'relative',
              padding: '22px 14px 20px',
              background: isRight
                ? 'var(--correct-soft)'
                : isWrong
                ? 'var(--wrong-soft)'
                : isSelected
                ? 'var(--amber-soft)'
                : 'var(--card)',
              border: isRight
                ? '2px solid var(--correct)'
                : isWrong
                ? '2px solid var(--wrong)'
                : isSelected
                ? '2px solid var(--amber)'
                : '2px solid rgba(60,50,40,0.15)',
              borderRadius: 4,
              cursor: submitted || disabled ? 'default' : 'pointer',
              transition: 'all 0.15s',
              fontFamily: 'var(--display)',
              fontSize: labelSize,
              fontWeight: 600,
              color: 'var(--ink)',
              opacity: disabled ? 0.4 : 1,
              transform: isSelected && !submitted ? 'translateY(-2px)' : 'none',
              boxShadow: isSelected && !submitted
                ? '0 6px 16px rgba(201,106,47,0.2)'
                : '0 1px 2px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {/* lantern hanger */}
            <div style={{
              position: 'absolute', top: -8, left: '50%',
              transform: 'translateX(-50%)',
              width: 1, height: 8,
              background: 'rgba(60,50,40,0.3)',
            }} />
            <div style={{
              position: 'absolute', top: -12, left: '50%',
              transform: 'translateX(-50%)',
              width: 8, height: 4,
              background: 'var(--ink-muted)',
              borderRadius: 1,
            }} />

            <div aria-hidden="true" style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.16em',
              color: isRight ? 'var(--correct)' : isWrong ? 'var(--wrong)' : 'var(--ink-muted)',
              textTransform: 'uppercase',
            }}>
              {letter}
            </div>

            <div style={{
              textAlign: 'center',
              wordBreak: 'break-word',
              hyphens: 'auto',
              lineHeight: 1.25,
              maxWidth: '100%',
            }}>{opt.label}</div>

            {isRight && (
              <div style={{
                position: 'absolute', top: 8, right: 10,
                fontFamily: 'var(--mono)',
                fontSize: 10,
                color: 'var(--correct)',
                fontWeight: 700,
                letterSpacing: '0.1em',
              }}>✓</div>
            )}
            {isWrong && (
              <div style={{
                position: 'absolute', top: 8, right: 10,
                fontFamily: 'var(--mono)',
                fontSize: 10,
                color: 'var(--wrong)',
                fontWeight: 700,
                letterSpacing: '0.1em',
              }}>✕</div>
            )}
          </button>
        );
      })}
    </div>
  );
}
